import type { PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { PsHttpService } from '@patsnap/tahiti-core/common/ps-http';
import psHttp from '@patsnap/common-ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';

export interface SandboxFileInfo {
    path: string;
    name: string;
    size: number;
    size_human: string;
    mime_type: string;
    extension: string;
    is_previewable: boolean;
    is_downloadable: boolean;
    last_modified?: string;
}

export interface SandboxFilesListRequest {
    session_id: string;
}

export interface SandboxFilesListResponse {
    session_id: string;
    files: SandboxFileInfo[];
    total_count: number;
    total_size: number;
    total_size_human: string;
}

export interface SandboxFileInfoRequest {
    session_id: string;
    path: string;
}

export interface SandboxFileReadRequest {
    session_id: string;
    path: string;
}

export interface SandboxFileReadResponse {
    path: string;
    content: string | null;
    size: number;
    size_human?: string;
    mime_type: string;
    is_binary?: boolean;
    truncated: boolean;
    error?: string;
}

export interface SandboxFileDownloadUrlRequest {
    session_id: string;
    path: string;
    expires?: number;
}

export interface SandboxFileDownloadUrlResponse {
    url: string;
    expires: number;
}

export interface SandboxFileExportRequest {
    session_id: string;
}

export interface SandboxFileValidateRequest {
    session_id: string;
    path: string;
}

export interface SandboxFileValidateResponse {
    valid: boolean;
    exists: boolean;
    path: string;
    file_info: SandboxFileInfo | null;
    error: string | null;
}

export interface SandboxFilesApi {
    fetchFilesList(data: SandboxFilesListRequest): Promise<SandboxFilesListResponse>;
    fetchFileInfo(data: SandboxFileInfoRequest): Promise<SandboxFileInfo>;
    fetchFileRead(data: SandboxFileReadRequest): Promise<SandboxFileReadResponse>;
    fetchFileDownloadUrl(data: SandboxFileDownloadUrlRequest): Promise<SandboxFileDownloadUrlResponse>;
    exportFiles(data: SandboxFileExportRequest): Promise<Blob>;
    validateFile(data: SandboxFileValidateRequest): Promise<SandboxFileValidateResponse>;
}

export const SandboxFilesApi = Symbol('SandboxFilesApi');

@injectable()
export class SandboxFilesApiImpl extends PsHttpService implements SandboxFilesApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/api/eureka/sandbox-files`,
        };
    }

    async fetchFilesList(data: SandboxFilesListRequest): Promise<SandboxFilesListResponse> {
        return this.post<PatSnapCommonResponseData<SandboxFilesListResponse>>({
            url: `${this.options.baseURL}/list`,
            data,
        }).then((res) => {
            return res.data as SandboxFilesListResponse;
        });
    }

    async fetchFileInfo(data: SandboxFileInfoRequest): Promise<SandboxFileInfo> {
        return this.post<PatSnapCommonResponseData<SandboxFileInfo>>({
            url: `${this.options.baseURL}/info`,
            data,
        }).then((res) => {
            return res.data as SandboxFileInfo;
        });
    }

    async fetchFileRead(data: SandboxFileReadRequest): Promise<SandboxFileReadResponse> {
        return this.post<PatSnapCommonResponseData<SandboxFileReadResponse>>({
            url: `${this.options.baseURL}/read`,
            data,
        }).then((res) => {
            return res.data as SandboxFileReadResponse;
        });
    }

    async fetchFileDownloadUrl(data: SandboxFileDownloadUrlRequest): Promise<SandboxFileDownloadUrlResponse> {
        return this.post<PatSnapCommonResponseData<SandboxFileDownloadUrlResponse>>({
            url: `${this.options.baseURL}/download-url`,
            data,
        }).then((res) => {
            return res.data as SandboxFileDownloadUrlResponse;
        });
    }

    async exportFiles(data: SandboxFileExportRequest): Promise<Blob> {
        const response = await psHttp({
            url: `${this.options.baseURL}/export`,
            method: 'POST',
            data,
            responseType: 'blob',
        });
        return response.data as Blob;
    }

    async validateFile(data: SandboxFileValidateRequest): Promise<SandboxFileValidateResponse> {
        return this.post<PatSnapCommonResponseData<SandboxFileValidateResponse>>({
            url: `${this.options.baseURL}/validate`,
            data,
        }).then((res) => {
            return res.data as SandboxFileValidateResponse;
        });
    }
}
